import { formatCnae, formatCnpj, formatCurrencyBRL, formatDateBR, truncate } from "@/lib/format";
import { MATRIZ_FILIAL_LABELS, UI } from "@/lib/labels";
import { cn } from "@/lib/utils";
import type { ResultItem } from "@/types/api";
import { Mail, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import { SituacaoBadge } from "./SituacaoBadge";

interface ResultsTableProps {
  items: ResultItem[];
  /** URL da pesquisa atual, preservada no link de detalhe (botão voltar). */
  detailFrom: string;
  /** Quando true, a razão social abre o drawer lateral em vez de navegar. */
  drawerMode: boolean;
  onOpenRecord: (cnpj: string) => void;
}

const headerClass = "px-3 py-2.5 font-medium";
const cellClass = "px-3 py-2.5 align-top";

/** Tabela de resultados da pesquisa. Rola horizontalmente em telas estreitas. */
export function ResultsTable({ items, detailFrom, drawerMode, onOpenRecord }: ResultsTableProps) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[68rem] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th className={headerClass}>{UI.table.razaoSocial}</th>
            <th className={headerClass}>{UI.table.nomeFantasia}</th>
            <th className={headerClass}>{UI.table.cnpj}</th>
            <th className={headerClass}>{UI.table.situacao}</th>
            <th className={headerClass}>{UI.table.local}</th>
            <th className={headerClass}>{UI.table.cnae}</th>
            <th className={headerClass}>{UI.table.abertura}</th>
            <th className={cn(headerClass, "text-right")}>{UI.table.capital}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr
              key={item.cnpj}
              className="border-b border-border/60 transition-colors hover:bg-muted/40"
            >
              <td className={cellClass}>
                {drawerMode ? (
                  <button
                    type="button"
                    onClick={() => onOpenRecord(item.cnpj)}
                    className="text-left font-medium text-accent hover:underline"
                  >
                    {item.razao_social || "—"}
                  </button>
                ) : (
                  <Link
                    to={`/cnpj/${item.cnpj}`}
                    state={{ from: detailFrom }}
                    className="font-medium text-accent hover:underline"
                  >
                    {item.razao_social || "—"}
                  </Link>
                )}
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {MATRIZ_FILIAL_LABELS[item.matriz_filial] ?? ""}
                </div>
              </td>
              <td className={cellClass}>
                <div className="flex items-center gap-2">
                  <span className="text-foreground">{item.nome_fantasia || "—"}</span>
                  <span className="flex shrink-0 items-center gap-1">
                    <span
                      role="img"
                      aria-label={item.tem_telefone ? "Com telefone" : "Sem telefone"}
                    >
                      <Phone
                        className={cn(
                          "h-3.5 w-3.5",
                          item.tem_telefone ? "text-primary" : "text-slate-300",
                        )}
                        aria-hidden="true"
                      />
                    </span>
                    <span role="img" aria-label={item.tem_email ? "Com e-mail" : "Sem e-mail"}>
                      <Mail
                        className={cn(
                          "h-3.5 w-3.5",
                          item.tem_email ? "text-primary" : "text-slate-300",
                        )}
                        aria-hidden="true"
                      />
                    </span>
                  </span>
                </div>
              </td>
              <td className={cn(cellClass, "whitespace-nowrap font-mono text-xs")}>
                {formatCnpj(item.cnpj)}
              </td>
              <td className={cellClass}>
                <SituacaoBadge codigo={item.situacao_cadastral} />
              </td>
              <td className={cn(cellClass, "whitespace-nowrap")}>
                <span className="font-medium text-foreground">{item.uf}</span>
                <span className="text-muted-foreground"> · {item.municipio_nome}</span>
              </td>
              <td className={cellClass}>
                <div className="font-mono text-xs text-muted-foreground">
                  {formatCnae(item.cnae_principal)}
                </div>
                <div className="text-xs text-foreground" title={item.cnae_descricao}>
                  {truncate(item.cnae_descricao ?? "", 46)}
                </div>
              </td>
              <td className={cn(cellClass, "whitespace-nowrap")}>
                {formatDateBR(item.data_inicio_atividade)}
              </td>
              <td className={cn(cellClass, "whitespace-nowrap text-right")}>
                {formatCurrencyBRL(item.capital_social)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
